﻿import {defineStore} from 'pinia'
import {useHostNameStore} from '@/stores/useHostNameStore'
import type {AppConfiguration} from '@/types'
import i18n from '@/locales/dictionary'

export const useAppConfigurationStore = defineStore('appconfiguration', {
    state: () => ({
        // current computed configuration
        configuration: getConfiguration(),
        // reactive timestamp updated when configuration is refreshed at runtime
        lastUpdated: Date.now(),

        isTidTilTur() {
            return getConfiguration().id.includes('tidtiltur.dk')
        },
        isAffaldsindsamlingen() {
            return getConfiguration().id.includes('affaldsindsamlingen.dk')
        },
        isLocalDepartment() {
            return getConfiguration().id.includes('lokaleevents.dn.dk')
        },

        isNaturensUge() {
            return getConfiguration().id.includes('naturensuge.dk')
        },       
        isDnV2() {
            return getConfiguration().id === 'arrangementer.dn.dk'
        }
        , isLocalhost() {
            return getConfiguration().id === 'localhost'
        },
        isDevelopment() {
            return getConfiguration().environment === 'development'
        },
        isStaging() {
            return getConfiguration().environment === 'staging'
        },
        isProduction() {
            return getConfiguration().environment === 'production'
        },
    }),

    actions: {
        // Recompute and replace the configuration at runtime (useful when hostname is switched in dev).
        // Return a promise so callers can await completion.
        async refreshConfiguration() {
            // recompute configuration based on current hostname store
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ;(this as any).configuration = getConfiguration()
            // bump the timestamp to make it easy for components to watch and react
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ;(this as any).lastUpdated = Date.now()
            return Promise.resolve()
        }
    }
})

function getConfiguration() {
    const hostNameStore = useHostNameStore()



    // default configuration
    var configuration: AppConfiguration = {
        id: hostNameStore.hostname,
        environment: hostNameStore.environment,
        showPreview: false,
        showProgressbar: true,
        logoUrl: '/images/logo/DN_Logo_3Linje_Sand.svg',
        showNewsletter: false,
        showImages: true,
        externalEventLinkUrl: "",
        header: {
            showHome: true,
            showEvents: true,
            showHelp: true,
            helpLink: 'https://aktiv.dn.dk/sadan-gor-vi/ture-og-arrangementer/annoncering-af-ture/',

            showCreateEvent: false,
            showMyEvents: true,
            showOrganizer: true,
            showOverview: true,
            logoLinkTo: 'events-search'
        },
        pages: {
            organizer: {showCreateOrganizer: true},
            createEvent: {showHeader: true},
            frontpage: {showEvents: true, showLogin: false}
        },
        paths: {
            home: '/min-side',
            myEvents: '/min-side/mine-arrangementer',
            eventCreated: '/min-side/mine-arrangementer/kvittering'
        }
    } as AppConfiguration



    // Normalize id for matching
    const id = (configuration.id || '').toLowerCase()

    // Handle specific preview host first: arrangementer.dn.dk (must come before generic arrangementer.dn.dk)
    if (id === 'arrangementer.dn.dk') {
        i18n.global.locale = 'dn'
        configuration.logoUrl = '/images/logo/DN_Logo_3Linje_Sand.png'
        // optional: mark preview mode
        configuration.showPreview = false
    }
    // General DN hosts (events, arrangementer, lokaleevents, localhost)
    else if (
      id === 'events.dn.dk' ||
      id === 'lokaleevents.dn.dk' ||
      id === 'localhost' ||
      id.endsWith('.dn.dk') && (id.includes('events') || id.includes('arrangementer') || id.includes('lokaleevents'))
    ) {
        i18n.global.locale = 'dn'
    }

    // Tid til tur configuration
    if (id.endsWith('tidtiltur.dk') || id.includes('tidtiltur.dk')) {
        i18n.global.locale = 'tidtiltur'

        configuration.showPreview = false
        configuration.showImages = false
        configuration.showNewsletter = false
        configuration.externalEventLinkUrl = 'https://tidtiltur.dk/tur/'
        configuration.header = {
            showHome: true,
            showEvents: false,
            showHelp: true,
            helpLink: 'https://tidtiltur.dk/hjaelp-til-turmodul',
            showCreateEvent: false,
            showMyEvents: true,
            showOverview: true,
            showOrganizer: false,
            logoLinkTo: 'home'
        }
        configuration.pages.organizer.showCreateOrganizer = false
        configuration.pages.frontpage = { showEvents: false, showLogin: true }
        configuration.paths = {
            home: '/min-side',
            myEvents: '/min-side/mine-ture',
            eventCreated: '/min-side/mine-ture/kvittering'
        }
        configuration.logoUrl = '/images/logo/ttt-logo.svg'
    } else if (id.endsWith('affaldsindsamlingen.dk') || id.includes('affaldsindsamlingen.dk')) {
        i18n.global.locale = 'affaldsindsamlingen'

        configuration.showPreview = false
        configuration.showProgressbar = false
        configuration.showImages = false
        configuration.showNewsletter = false
        configuration.externalEventLinkUrl = ''
        configuration.header = {
            showHome: true,
            showEvents: true,
            showHelp: true,
            helpLink: 'https://www.affaldsindsamlingen.dk/hjaelp-til-oprettelse/',
            showCreateEvent: false,
            showMyEvents: true,
            showOverview: true,
            showOrganizer: true,
            logoLinkTo: 'home'
        }
        configuration.paths = {
            home: '/min-side',
            myEvents: '/min-side/mine-indsamlinger',
            eventCreated: '/min-side/mine-indsamlinger/kvittering'
        }
        configuration.pages.organizer.showCreateOrganizer = false
        configuration.pages.frontpage = { showEvents: false, showLogin: true }
        configuration.pages.createEvent.showHeader = false
        configuration.logoUrl = '/images/logo/DN_Logo_3Linje_Sand.png'
    } else if (id.endsWith('naturensuge.dk') || id.includes('naturensuge.dk')) {
        i18n.global.locale = 'naturensuge'

        configuration.showPreview = false
        configuration.showProgressbar = false
        configuration.showImages = true
        configuration.showNewsletter = false
        configuration.externalEventLinkUrl = ''
        configuration.header = {
            showHome: true,
            showEvents: false,
            showHelp: true,
            helpLink: 'https://www.naturensuge.dk/arrangoerer/#FAQ',
            showCreateEvent: false,
            showMyEvents: true,
            showOverview: true,
            showOrganizer: true,
            logoLinkTo: 'home'
        }
        configuration.paths = {
            home: '/min-side',
            myEvents: '/min-side/mine-arrangementer',
            eventCreated: '/min-side/mine-arrangementer/kvittering'
        }
        configuration.pages.organizer.showCreateOrganizer = false
        configuration.pages.createEvent.showHeader = false
        configuration.pages.frontpage = { showEvents: false, showLogin: true }
        configuration.logoUrl = '/images/logo/naturensuge-logo.png'
    }


    
    return configuration
}
